import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  FaArrowLeft,
  FaBell,
  FaEdit,
  FaExclamationTriangle,
  FaRegCheckCircle,
  FaSnowflake,
} from "react-icons/fa";
import { getMerchantProfile } from "../api/api";
import "../styles/MerchantProfilePage.css";

const fmtMoney = (value) => {
  const n = Number(String(value ?? "").replace(/[^\d.]/g, ""));
  if (!Number.isFinite(n)) return "Rs 0";
  return `Rs ${n.toLocaleString()}`;
};

const asNumber = (value, fallback = 0) => {
  const n = Number(String(value ?? "").replace(/[^\d.]/g, ""));
  return Number.isFinite(n) ? n : fallback;
};

const statusLabel = (status = "") => {
  const raw = String(status).toLowerCase();
  if (["active", "updated"].includes(raw)) return "Active";
  if (["pending", "review"].includes(raw)) return "Pending";
  return "Blocked";
};

const MerchantProfile = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state || {};
  const stateMerchant = state?.merchant || null;
  const merchantID = stateMerchant?.merchantID || "";

  const [merchant, setMerchant] = useState(stateMerchant);
  const [loading, setLoading] = useState(!stateMerchant);

  useEffect(() => {
    if (merchant || !merchantID) return;

    const fetchData = async () => {
      try {
        const res = await getMerchantProfile(merchantID);
        if (res?.isSuccess && res?.data) setMerchant(res.data);
      } catch (err) {
        console.error("Error loading merchant profile", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [merchant, merchantID]);

  const view = useMemo(() => {
    const m = merchant || {};
    const totalVolume = asNumber(m.totalVolume ?? m.volume, 0);
    const revenueShare = asNumber(m.revenueShare ?? m.monthlyRevenue, 0);
    const qrGrowth = asNumber(m.qrGrowth ?? 84, 84);
    const successRate = asNumber(m.successRate ?? 99.8, 99.8);
    const riskScore = asNumber(m.riskScore ?? 12, 12);
    const staticQr = asNumber(m.staticQr ?? m.staticQrCount ?? 72, 72);
    const dynamicQr = asNumber(m.dynamicQr ?? m.dynamicQrCount ?? 28, 28);

    const chartBars = [26, 34, 29, 44, 58, 41, 31, 51, 38, 29, 48, 41];

    const riskAlerts = [
      {
        title: "Velocity Spike Detected",
        desc: "Transaction volume exceeded 200% threshold within 1 hour.",
      },
      {
        title: "Geographic Shift",
        desc: "First dynamic payment from non-native region.",
      },
    ];

    const recentTransactions = (m.recentTransactions || [
      { id: "#TX-9012", method: "Apple Pay", amount: "Rs 0", status: "SUCCESSFUL", date: "Today, 14:22" },
      { id: "#TX-9011", method: "Mastercard", amount: "Rs 0", status: "SUCCESSFUL", date: "Today, 14:10" },
      { id: "#TX-9010", method: "Visa", amount: "Rs 0", status: "DECLINED", date: "Today, 13:55" },
      { id: "#TX-9009", method: "Dynamic QR", amount: "Rs 0", status: "SUCCESSFUL", date: "Yesterday, 18:30" },
    ]).slice(0, 4);

    const activityLog = [
      { title: "Limit increased", when: "System • 12 mins ago" },
      { title: "KYC Level 3 Cleared", when: "Admin Sarah • 4h ago" },
      { title: "Auto-Settlement Batch Start", when: "System • 09:00 AM" },
    ];

    return {
      name: m.merchantName || "Velvet Coffee Co.",
      id: m.merchantID || "MF-98231",
      sector: m.categoryName || m.merchantCategoryCode || "Premium Food & Beverage",
      assignedAgent: m.assignedAgent || m.agentName || "James D. Wilson",
      status: statusLabel(m.status),
      totalVolume,
      revenueShare,
      qrGrowth,
      successRate,
      riskScore,
      staticQr,
      dynamicQr,
      chartBars,
      riskAlerts,
      recentTransactions,
      activityLog,
    };
  }, [merchant]);

  if (loading) {
    return (
      <div className="mpf-page">
        <p className="mpf-loading">Loading merchant profile...</p>
      </div>
    );
  }

  if (!merchant && !stateMerchant) {
    return (
      <div className="mpf-page">
        <button type="button" className="mpf-back" onClick={() => navigate(-1)}>
          <FaArrowLeft /> Back
        </button>
        <p className="mpf-loading">Merchant not found.</p>
      </div>
    );
  }

  return (
    <div className="mpf-page">
      <button type="button" className="mpf-back" onClick={() => navigate(-1)}>
        <FaArrowLeft /> Back to Merchants
      </button>

      <div className="mpf-head">
        <div>
          <h1>{view.name}</h1>
          <div className="mpf-subline">
            <span className="mpf-chip">{view.status}</span>
            <span>Risk: {view.riskScore}/100 (Low)</span>
          </div>
          <p>Merchant ID: {view.id} • Sector: {view.sector}</p>
        </div>

        <div className="mpf-head-actions">
          <button type="button"><FaBell /> Message</button>
          <button type="button"><FaEdit /> Edit</button>
          <button type="button" className="danger"><FaSnowflake /> Freeze</button>
        </div>
      </div>

      <div className="mpf-kpis">
        <div className="mpf-kpi"><label>Total Volume</label><strong>{fmtMoney(view.totalVolume)}</strong><span>0%</span></div>
        <div className="mpf-kpi"><label>Revenue Share</label><strong>{fmtMoney(view.revenueShare)}</strong><span>Net Margin 0%</span></div>
        <div className="mpf-kpi"><label>QR Activity</label><strong>{view.qrGrowth}% Growth</strong><span>High Usage</span></div>
        <div className="mpf-kpi"><label>Success Rate</label><strong>{view.successRate}%</strong><span>Optimized</span></div>
      </div>

      <div className="mpf-grid">
        <div className="mpf-main">
          <section className="mpf-card mpf-card--trend">
            <div className="mpf-card-head">
              <h3>Transaction Trends</h3>
              <div className="mpf-pills"><span className="active">30 Days</span><span>90 Days</span></div>
            </div>
            <div className="mpf-bars">
              {view.chartBars.map((height, idx) => (
                <span key={idx} style={{ height: `${height}%` }} />
              ))}
            </div>
          </section>

          <div className="mpf-row2">
            <section className="mpf-card">
              <h3>QR Analytics</h3>
              <div className="mpf-rings">
                <div>
                  <span>{view.staticQr}%</span>
                  <p>Static QR</p>
                </div>
                <div>
                  <span>{view.dynamicQr}%</span>
                  <p>Dynamic QR</p>
                </div>
              </div>
            </section>

            <section className="mpf-card">
              <h3>Assigned Agent</h3>
              <div className="mpf-agent">
                <div className="mpf-agent-avatar">{view.assignedAgent.slice(0, 1)}</div>
                <div>
                  <strong>{view.assignedAgent}</strong>
                  <small>Senior Account Manager</small>
                </div>
              </div>
              <div className="mpf-agent-actions">
                <button type="button" className="primary">Quick Call</button>
                <button type="button">Email</button>
              </div>
            </section>
          </div>

          <section className="mpf-card">
            <h3>Settlement Timeline (Batch #4412)</h3>
            <div className="mpf-timeline">
              <div className="done">Calculated</div>
              <div className="done">Verified</div>
              <div className="live">Processing</div>
              <div>Settled</div>
            </div>
          </section>

          <section className="mpf-card">
            <div className="mpf-card-head"><h3>Recent Transactions</h3><button type="button">Export CSV</button></div>
            <table className="mpf-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Method</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {view.recentTransactions.map((txn) => (
                  <tr key={txn.id}>
                    <td>{txn.id}</td>
                    <td>{txn.method}</td>
                    <td>{txn.amount}</td>
                    <td>
                      <span className={`mpf-badge ${txn.status === "SUCCESSFUL" ? "ok" : "bad"}`}>
                        {txn.status}
                      </span>
                    </td>
                    <td>{txn.date}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        </div>

        <aside className="mpf-side">
          <section className="mpf-card">
            <h3><FaExclamationTriangle /> Risk Alerts</h3>
            <div className="mpf-alerts">
              {view.riskAlerts.map((alert) => (
                <article key={alert.title}>
                  <strong>{alert.title}</strong>
                  <p>{alert.desc}</p>
                </article>
              ))}
            </div>
            <button type="button" className="mpf-side-btn">View Incident Map</button>
          </section>

          <section className="mpf-card">
            <h3>Admin Notes</h3>
            <div className="mpf-note">Add a private note...</div>
          </section>

          <section className="mpf-card">
            <h3>Quick Controls</h3>
            <div className="mpf-switch"><span>Terminal Access</span><input type="checkbox" defaultChecked readOnly /></div>
            <div className="mpf-switch"><span>Payout Automation</span><input type="checkbox" defaultChecked readOnly /></div>
            <div className="mpf-switch"><span>Risk Shield</span><input type="checkbox" readOnly /></div>
          </section>

          <section className="mpf-card">
            <h3>Activity Log</h3>
            <div className="mpf-logs">
              {view.activityLog.map((log) => (
                <div key={log.title}>
                  <FaRegCheckCircle />
                  <div>
                    <strong>{log.title}</strong>
                    <small>{log.when}</small>
                  </div>
                </div>
              ))}
            </div>
          </section>
        </aside>
      </div>
    </div>
  );
};

export default MerchantProfile;
